import React, { useEffect, useState } from "react";
import Page from "../../../components/Page";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Paper,
  Stack,
  TextField,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  Skeleton,
  TablePagination,
  Grid
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";

const LiveTestRules = () => {
  const token = localStorage.getItem("token");
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  // State
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggeringId, setTriggeringId] = useState(null);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  // Form Fields
  const [titlePrefix, setTitlePrefix] = useState("");
  const [course, setCourse] = useState("");
  const [duration, setDuration] = useState("");
  const [eachQuestionMarks, setEachQuestionMarks] = useState("2.0");
  const [nagetiveMarking, setNegativeMarking] = useState("true");
  const [nagetiveMarks, setNegativeMarks] = useState("0.5");
  const [language, setLanguage] = useState("en");
  const [medium, setMedium] = useState("3");
  const [cronExpression, setCronExpression] = useState("0 9 * * *");
  const [sendNotification, setSendNotification] = useState(true);
  const [isActive, setIsActive] = useState(true);

  // User-friendly scheduling states
  const [frequency, setFrequency] = useState("daily");
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [dayOfWeek, setDayOfWeek] = useState("0");
  const [dayOfMonth, setDayOfMonth] = useState("1");
  const [customCron, setCustomCron] = useState("0 9 * * *");

  const parseCronToUI = (cronStr) => {
    if (!cronStr) return { frequency: "daily", time: "09:00", dayOfWeek: "0", dayOfMonth: "1", customCron: "0 9 * * *" };
    const parts = cronStr.trim().split(/\s+/);
    if (parts.length < 5) return { frequency: "custom", time: "09:00", dayOfWeek: "0", dayOfMonth: "1", customCron: cronStr };
    
    const [min, hour, dayOfMonthVal, month, dayOfWeekVal] = parts;
    
    const formatTime = (h, m) => {
      const hh = parseInt(h, 10).toString().padStart(2, '0');
      const mm = parseInt(m, 10).toString().padStart(2, '0');
      return `${hh}:${mm}`;
    };

    const isNumber = (val) => !isNaN(val) && val !== "*";

    if (isNumber(min) && isNumber(hour)) {
      const timeVal = formatTime(hour, min);
      if (dayOfMonthVal === "*" && month === "*" && dayOfWeekVal === "*") {
        return { frequency: "daily", time: timeVal, dayOfWeek: "0", dayOfMonth: "1", customCron: cronStr };
      }
      if (dayOfMonthVal === "*" && month === "*" && dayOfWeekVal !== "*") {
        return { frequency: "weekly", time: timeVal, dayOfWeek: dayOfWeekVal, dayOfMonth: "1", customCron: cronStr };
      }
      if (dayOfMonthVal !== "*" && month === "*" && dayOfWeekVal === "*") {
        return { frequency: "monthly", time: timeVal, dayOfWeek: "0", dayOfMonth: dayOfMonthVal, customCron: cronStr };
      }
    }
    
    return { frequency: "custom", time: "09:00", dayOfWeek: "0", dayOfMonth: "1", customCron: cronStr };
  };

  const generateCronFromUI = (freq, timeVal, dow, dom, customVal) => {
    if (freq === "custom") return customVal || "0 9 * * *";
    
    const [hour, min] = (timeVal || "09:00").split(":").map(x => parseInt(x, 10));
    
    if (freq === "daily") {
      return `${min} ${hour} * * *`;
    }
    if (freq === "weekly") {
      return `${min} ${hour} * * ${dow}`;
    }
    if (freq === "monthly") {
      return `${min} ${hour} ${dom} * *`;
    }
    return "0 9 * * *";
  };

  // Exam Selection states
  const [subCategories, setSubCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [boards, setBoards] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedBoard, setSelectedBoard] = useState("");
  const [selectedExamId, setSelectedExamId] = useState("");

  // Subjects for blueprint
  const [subjectList, setSubjectList] = useState([]);
  const [blueprint, setBlueprint] = useState([
    { subject_id: "", question_count: 10, chapters: "" }
  ]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LIVE_URL_NEW}/admin/live-test-rules`,
        { headers }
      );
      if (response.data?.response_code === 200) {
        setRules(response.data?.data || []);
      } else {
        toast.error("Failed to fetch rules");
      }
    } catch (error) {
      console.error("Error fetching rules:", error);
      toast.error("Error loading Live Test Rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      // Pre-fetch state exams, states, boards if needed
      const stateList = await axios.post(
        `${process.env.REACT_APP_LIVE_URL_NEW}/mobile/get-states`,
        {},
        { headers }
      );
      setStates(stateList?.data?.data || []);

      const boardsList = await axios.post(
        `${process.env.REACT_APP_LIVE_URL_NEW}/mobile/get-boards`,
        {},
        { headers }
      );
      setBoards(boardsList?.data?.data || []);
    } catch (e) {
      console.error("Error pre-loading dropdowns:", e);
    }
  };

  const handleCategoryChange = async (categoryVal) => {
    setCourse(categoryVal);
    setSubCategories([]);
    setSelectedState("");
    setSelectedBoard("");
    setSelectedExamId("");
    setSubjectList([]);

    try {
      if (categoryVal === "central-exam") {
        const res = await axios.post(
          `${process.env.REACT_APP_LIVE_URL_NEW}/mobile/get-central-exams`,
          {},
          { headers }
        );
        setSubCategories(res?.data?.data || []);
      }
    } catch (error) {
      console.error("Error loading central exams:", error);
    }
  };

  const handleStateChange = async (stateVal) => {
    setSelectedState(stateVal);
    setSubCategories([]);
    setSelectedExamId("");
    setSubjectList([]);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_LIVE_URL_NEW}/mobile/get-state-exams`,
        { state_exam_id: stateVal },
        { headers }
      );
      setSubCategories(res?.data?.data || []);
    } catch (error) {
      console.error("Error loading state exams:", error);
    }
  };

  const handleBoardChange = async (boardVal) => {
    setSelectedBoard(boardVal);
    setSubCategories([]);
    setSelectedExamId("");
    setSubjectList([]);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_LIVE_URL_NEW}/mobile/get-classes`,
        { board_id: boardVal },
        { headers }
      );
      setSubCategories(res?.data?.data || []);
    } catch (error) {
      console.error("Error loading classes:", error);
    }
  };

  const handleExamChange = async (examVal) => {
    setSelectedExamId(examVal);
    // Fetch subjects matching this selected exam / class
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_LIVE_URL_NEW}/admin/get-subject-by-goal`,
        {
          course: course,
          language: language,
          class_exam_id: examVal
        },
        { headers }
      );
      if (res.data?.response_code === 200) {
        const filtered = res.data?.data?.map((sub) => ({
          id: sub.subject_id,
          name: sub.subject_name
        })) || [];
        setSubjectList(filtered);
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const handleAddBlueprintRow = () => {
    setBlueprint([...blueprint, { subject_id: "", question_count: 10, chapters: "" }]);
  };

  const handleRemoveBlueprintRow = (index) => {
    setBlueprint(blueprint.filter((_, idx) => idx !== index));
  };

  const handleBlueprintRowChange = (index, key, val) => {
    const updated = blueprint.map((item, idx) => {
      if (idx === index) {
        return { ...item, [key]: val };
      }
      return item;
    });
    setBlueprint(updated);
  };

  const handleOpenAddModal = () => {
    setEditingRule(null);
    setTitlePrefix("");
    setCourse("");
    setDuration("");
    setEachQuestionMarks("2.0");
    setNegativeMarking("true");
    setNegativeMarks("0.5");
    setLanguage("en");
    setMedium("3");
    setCronExpression("0 9 * * *");
    setSendNotification(true);
    setIsActive(true);
    setSelectedState("");
    setSelectedBoard("");
    setSelectedExamId("");
    setBlueprint([{ subject_id: "", question_count: 10, chapters: "" }]);
    setFrequency("daily");
    setScheduleTime("09:00");
    setDayOfWeek("0");
    setDayOfMonth("1");
    setCustomCron("0 9 * * *");
    setOpenDialog(true);
  };

  const handleOpenEditModal = async (rule) => {
    setEditingRule(rule);
    setTitlePrefix(rule.title_prefix);
    setCourse(rule.course);
    setDuration(rule.duration);
    setEachQuestionMarks(rule.eachQuestionMarks);
    setNegativeMarking(rule.nagetiveMarking);
    setNegativeMarks(rule.nagetiveMarks);
    setLanguage(rule.language);
    setMedium(rule.medium?.toString() || "3");
    setCronExpression(rule.cron_expression);
    setSendNotification(rule.send_notification === 1 || rule.send_notification === true);
    setIsActive(rule.is_active === 1 || rule.is_active === true);

    const uiStates = parseCronToUI(rule.cron_expression);
    setFrequency(uiStates.frequency);
    setScheduleTime(uiStates.time);
    setDayOfWeek(uiStates.dayOfWeek);
    setDayOfMonth(uiStates.dayOfMonth);
    setCustomCron(uiStates.customCron);

    // Dynamic dropdown loading logic
    try {
      if (rule.course === "central-exam") {
        const res = await axios.post(
          `${process.env.REACT_APP_LIVE_URL_NEW}/mobile/get-central-exams`,
          {},
          { headers }
        );
        setSubCategories(res?.data?.data || []);
        setSelectedExamId(rule.exam_id);
      } else if (rule.course === "state-exam") {
        setSelectedState(rule.state_exam_id);
        const res = await axios.post(
          `${process.env.REACT_APP_LIVE_URL_NEW}/mobile/get-state-exams`,
          { state_exam_id: rule.state_exam_id },
          { headers }
        );
        setSubCategories(res?.data?.data || []);
        setSelectedExamId(rule.exam_id);
      } else if (rule.course === "class") {
        setSelectedBoard(rule.board_id);
        const res = await axios.post(
          `${process.env.REACT_APP_LIVE_URL_NEW}/mobile/get-classes`,
          { board_id: rule.board_id },
          { headers }
        );
        setSubCategories(res?.data?.data || []);
        setSelectedExamId(rule.class_id);
      }

      // Fetch subjects matching selected exam
      const examVal = rule.course === "class" ? rule.class_id : rule.exam_id;
      const subRes = await axios.post(
        `${process.env.REACT_APP_LIVE_URL_NEW}/admin/get-subject-by-goal`,
        {
          course: rule.course,
          language: rule.language,
          class_exam_id: examVal
        },
        { headers }
      );
      if (subRes.data?.response_code === 200) {
        const filtered = subRes.data?.data?.map((sub) => ({
          id: sub.subject_id,
          name: sub.subject_name
        })) || [];
        setSubjectList(filtered);
      }

      // Parse blueprint
      let parsedBlueprint = rule.blueprint;
      if (typeof parsedBlueprint === "string") {
        parsedBlueprint = JSON.parse(parsedBlueprint);
      }
      const formattedBlueprint = (parsedBlueprint || []).map((b) => ({
        subject_id: b.subject_id,
        question_count: b.question_count || 10,
        chapters: b.chapters ? b.chapters.join(",") : ""
      }));
      setBlueprint(formattedBlueprint.length > 0 ? formattedBlueprint : [{ subject_id: "", question_count: 10, chapters: "" }]);

    } catch (e) {
      console.error("Error mapping edit values:", e);
    }

    setOpenDialog(true);
  };

  const handleSaveRule = async () => {
    if (!titlePrefix || !course || !duration || !selectedExamId) {
      toast.error("Please fill in all required fields!");
      return;
    }

    // Format blueprint array back to backend expected structure
    const formattedBlueprint = blueprint.map((item) => {
      const chaptersArr = item.chapters
        ? item.chapters.split(",").map((c) => parseInt(c.trim())).filter((c) => !isNaN(c))
        : [];
      return {
        subject_id: parseInt(item.subject_id),
        question_count: parseInt(item.question_count),
        chapters: chaptersArr
      };
    });

    const payload = {
      title_prefix: titlePrefix,
      course: course,
      duration: parseInt(duration),
      eachQuestionMarks: parseFloat(eachQuestionMarks),
      nagetiveMarking: nagetiveMarking,
      nagetiveMarks: parseFloat(nagetiveMarks),
      language: language,
      medium: medium,
      cron_expression: generateCronFromUI(frequency, scheduleTime, dayOfWeek, dayOfMonth, customCron),
      send_notification: sendNotification,
      is_active: isActive,
      blueprint: formattedBlueprint,
      exam_groups_id: course === "central-exam" ? 1 : course === "state-exam" ? 2 : 3,
      board_id: course === "class" ? selectedBoard : null,
      class_id: course === "class" ? selectedExamId : null,
      state_exam_id: course === "state-exam" ? selectedState : null,
      exam_id: course === "class" ? null : selectedExamId
    };

    try {
      let res;
      if (editingRule) {
        res = await axios.put(
          `${process.env.REACT_APP_LIVE_URL_NEW}/admin/live-test-rules/${editingRule.id}`,
          payload,
          { headers }
        );
      } else {
        res = await axios.post(
          `${process.env.REACT_APP_LIVE_URL_NEW}/admin/live-test-rules`,
          payload,
          { headers }
        );
      }

      if (res.data?.response_code === 200) {
        toast.success(editingRule ? "Rule updated successfully!" : "Rule created successfully!");
        setOpenDialog(false);
        fetchRules();
      } else {
        toast.error(res.data?.message || "Failed to save rule");
      }
    } catch (e) {
      console.error("Failed to save rule:", e);
      toast.error(e.response?.data?.message || "Error saving live test scheduling rule");
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm("Are you sure you want to delete this rule?")) {
      return;
    }
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_LIVE_URL_NEW}/admin/live-test-rules/${id}`,
        { headers }
      );
      if (res.data?.response_code === 200) {
        toast.success("Rule deleted successfully!");
        fetchRules();
      }
    } catch (e) {
      console.error("Error deleting rule:", e);
      toast.error("Failed to delete rule");
    }
  };

  const handleTriggerRule = async (id) => {
    setTriggeringId(id);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_LIVE_URL_NEW}/admin/live-test-rules/${id}/trigger`,
        {},
        { headers }
      );
      if (res.data?.response_code === 200) {
        toast.success("Live Test generated successfully!");
      } else {
        toast.error("Failed to generate Live Test");
      }
    } catch (e) {
      console.error("Error triggering rule:", e);
      toast.error("Error triggering live test generation");
    } finally {
      setTriggeringId(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Page title="Live Test Rules">
        <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom="1.5rem">
          <Typography className="page-heading">Live Test Automation Settings</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddModal}
            sx={{
              background: "#4f46e5",
              "&:hover": { background: "#4338ca" }
            }}
          >
            Add Rule
          </Button>
        </Stack>

        <TableContainer component={Paper} sx={{ width: "100%" }}>
          <Table>
            <TableHead style={{ backgroundColor: "#e6e6ff" }}>
              <TableRow>
                <TableCell>Rule Prefix</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Cron Timing</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Send Push</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, idx) => (
                  <TableRow key={idx}>
                    <TableCell><Skeleton width="60%" /></TableCell>
                    <TableCell><Skeleton width="40%" /></TableCell>
                    <TableCell><Skeleton width="50%" /></TableCell>
                    <TableCell><Skeleton width="30%" /></TableCell>
                    <TableCell><Skeleton width="20%" /></TableCell>
                    <TableCell><Skeleton width="40%" /></TableCell>
                    <TableCell align="center"><Skeleton width="70%" /></TableCell>
                  </TableRow>
                ))
              ) : rules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No Live Test automation rules registered.
                  </TableCell>
                </TableRow>
              ) : (
                rules.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>{rule.title_prefix}</TableCell>
                    <TableCell style={{ textTransform: "capitalize" }}>{rule.course}</TableCell>
                    <TableCell><code>{rule.cron_expression}</code></TableCell>
                    <TableCell>{rule.duration} mins</TableCell>
                    <TableCell>
                      <Switch checked={rule.send_notification === 1 || rule.send_notification === true} disabled />
                    </TableCell>
                    <TableCell>
                      <Button
                        color={rule.is_active ? "success" : "error"}
                        variant="outlined"
                        size="small"
                        disabled
                      >
                        {rule.is_active ? "Active" : "Inactive"}
                      </Button>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="success"
                        onClick={() => handleTriggerRule(rule.id)}
                        disabled={triggeringId === rule.id}
                        title="Generate Live Test Now"
                      >
                        <PlayArrowIcon />
                      </IconButton>
                      <IconButton color="primary" onClick={() => handleOpenEditModal(rule)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDeleteRule(rule.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rules.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        {/* Create/Edit Rule Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>{editingRule ? "Edit Automation Rule" : "Create New Live Test Automation Rule"}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Title Prefix (e.g. Daily Live Mock)"
                  value={titlePrefix}
                  onChange={(e) => setTitlePrefix(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={course}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="central-exam">Central Exams</MenuItem>
                    <MenuItem value="state-exam">State Exams</MenuItem>
                    <MenuItem value="class">School Class/Board</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Dynamic selectors based on course type */}
              {course === "state-exam" && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>State</InputLabel>
                    <Select
                      value={selectedState}
                      onChange={(e) => handleStateChange(e.target.value)}
                      label="State"
                    >
                      {states.map((st) => (
                        <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {course === "class" && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Board</InputLabel>
                    <Select
                      value={selectedBoard}
                      onChange={(e) => handleBoardChange(e.target.value)}
                      label="Board"
                    >
                      {boards.map((bd) => (
                        <MenuItem key={bd.id} value={bd.id}>{bd.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {course && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>{course === "class" ? "Class" : "Exam"}</InputLabel>
                    <Select
                      value={selectedExamId}
                      onChange={(e) => handleExamChange(e.target.value)}
                      label={course === "class" ? "Class" : "Exam"}
                    >
                      {subCategories.map((sc) => (
                        <MenuItem key={sc.id} value={sc.id}>{sc.name || sc.class_name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <TextField
                  label="Duration (Minutes)"
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Medium</InputLabel>
                  <Select
                    value={medium}
                    onChange={(e) => setMedium(e.target.value)}
                    label="Medium"
                  >
                    <MenuItem value="1">English</MenuItem>
                    <MenuItem value="2">Hindi</MenuItem>
                    <MenuItem value="3">Bilingual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Marks Per Question"
                  value={eachQuestionMarks}
                  onChange={(e) => setEachQuestionMarks(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Negative Marking</InputLabel>
                  <Select
                    value={nagetiveMarking}
                    onChange={(e) => setNegativeMarking(e.target.value)}
                    label="Negative Marking"
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Negative Marks"
                  value={nagetiveMarks}
                  onChange={(e) => setNegativeMarks(e.target.value)}
                  fullWidth
                  disabled={nagetiveMarking === "false"}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    label="Frequency"
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="custom">Custom (Cron Expression)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {frequency !== "custom" && (
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
              )}

              {frequency === "weekly" && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel>Day of Week</InputLabel>
                    <Select
                      value={dayOfWeek}
                      onChange={(e) => setDayOfWeek(e.target.value)}
                      label="Day of Week"
                    >
                      <MenuItem value="0">Sunday</MenuItem>
                      <MenuItem value="1">Monday</MenuItem>
                      <MenuItem value="2">Tuesday</MenuItem>
                      <MenuItem value="3">Wednesday</MenuItem>
                      <MenuItem value="4">Thursday</MenuItem>
                      <MenuItem value="5">Friday</MenuItem>
                      <MenuItem value="6">Saturday</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {frequency === "monthly" && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel>Day of Month</InputLabel>
                    <Select
                      value={dayOfMonth}
                      onChange={(e) => setDayOfMonth(e.target.value)}
                      label="Day of Month"
                    >
                      {Array.from({ length: 28 }).map((_, i) => (
                        <MenuItem key={i + 1} value={String(i + 1)}>
                          {i + 1}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {frequency === "custom" && (
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Cron Expression (e.g. 0 9 * * *)"
                    value={customCron}
                    onChange={(e) => setCustomCron(e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
              )}

              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendNotification}
                      onChange={(e) => setSendNotification(e.target.checked)}
                    />
                  }
                  label="Send Notifications"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                  }
                  label="Is Active Rule"
                />
              </Grid>

              {/* Blueprint Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
                  Automation Blueprint (Subjects & Questions)
                </Typography>
                {blueprint.map((row, index) => (
                  <Stack key={index} direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <FormControl sx={{ minWidth: 200 }} required>
                      <InputLabel>Subject</InputLabel>
                      <Select
                        value={row.subject_id}
                        onChange={(e) => handleBlueprintRowChange(index, "subject_id", e.target.value)}
                        label="Subject"
                      >
                        {subjectList.map((sub) => (
                          <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      label="Question Count"
                      type="number"
                      value={row.question_count}
                      onChange={(e) => handleBlueprintRowChange(index, "question_count", e.target.value)}
                      sx={{ width: 150 }}
                      required
                    />

                    <TextField
                      label="Chapters (e.g., 12,13)"
                      placeholder="Optional"
                      value={row.chapters}
                      onChange={(e) => handleBlueprintRowChange(index, "chapters", e.target.value)}
                      sx={{ width: 250 }}
                    />

                    <IconButton color="error" onClick={() => handleRemoveBlueprintRow(index)} disabled={blueprint.length === 1}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                ))}
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddBlueprintRow}>
                  Add Subject
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveRule}>Save Rule</Button>
          </DialogActions>
        </Dialog>
      </Page>
      <ToastContainer />
    </>
  );
};

export default LiveTestRules;
